import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";

const links = [
  { label: "How It Works", href: "/how-it-works" },
  { label: "Solutions", href: "/solutions" },
  { label: "Why UniqReach", href: "/why-uniqreach" },
  { label: "Results", href: "/results" },
  { label: "About", href: "/about" },
];
const technology = [
  { label: "AI & Automation", href: "/technology/ai-automation" },
  { label: "Data & Prospecting", href: "/technology/data-prospecting" },
  {
    label: "Outbound Infrastructure",
    href: "/technology/outbound-infrastructure",
  },
];

export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const location = useLocation();
  const dropdown = useRef<HTMLDivElement>(null);
  const techButton = useRef<HTMLButtonElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setMenuOpen(false);
    setTechOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      if (dropdown.current && !dropdown.current.contains(event.target as Node))
        setTechOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (techOpen) {
          setTechOpen(false);
          techButton.current?.focus();
        } else if (menuOpen) {
          setMenuOpen(false);
          menuButton.current?.focus();
        }
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [techOpen, menuOpen]);
  useEffect(() => {
    const screen = window.matchMedia("(min-width: 1180px)");
    const close = () => {
      if (screen.matches) setMenuOpen(false);
    };
    screen.addEventListener("change", close);
    return () => screen.removeEventListener("change", close);
  }, []);
  const active = (href: string) =>
    location.pathname === href ? ("page" as const) : undefined;
  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="site-header">
        <nav className="container nav-inner" aria-label="Main navigation">
          <Link to="/" className="brand-link" aria-label="UniqReach home">
            <img
              src="/uniqreach-mark.svg"
              className="brand-mark"
              width="58"
              height="30"
              alt=""
            />
            <span>UniqReach</span>
          </Link>
          <div className="desktop-nav">
            {links.slice(0, 2).map((item) => (
              <Link
                to={item.href}
                key={item.href}
                aria-current={active(item.href)}
              >
                {item.label}
              </Link>
            ))}
            <div
              className="nav-technology"
              ref={dropdown}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget))
                  setTechOpen(false);
              }}
            >
              <button
                ref={techButton}
                type="button"
                onClick={() => setTechOpen(!techOpen)}
                aria-expanded={techOpen}
                aria-controls="technology-links"
                className={
                  location.pathname.startsWith("/technology/")
                    ? "is-active"
                    : ""
                }
              >
                Technology
                <ChevronDown size={15} aria-hidden="true" />
              </button>
              {techOpen && (
                <div id="technology-links" className="nav-dropdown">
                  {technology.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      aria-current={active(item.href)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {links.slice(2).map((item) => (
              <Link
                to={item.href}
                key={item.href}
                aria-current={active(item.href)}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <Link to="/book" className="btn btn-primary btn-sm nav-cta">
            Book a Strategy Call
            <ArrowRight size={15} aria-hidden="true" />
          </Link>
          <button
            ref={menuButton}
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? (
              <X size={25} aria-hidden="true" />
            ) : (
              <Menu size={25} aria-hidden="true" />
            )}
          </button>
        </nav>
        {menuOpen && (
          <nav
            id="mobile-menu"
            className="mobile-navigation container"
            aria-label="Mobile navigation"
            onClick={(event) => {
              if ((event.target as Element).closest("a")) setMenuOpen(false);
            }}
          >
            {links.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                aria-current={active(item.href)}
              >
                {item.label}
              </Link>
            ))}
            <span className="label">Technology</span>
            {technology.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                aria-current={active(item.href)}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/book" className="btn btn-primary">
              Book a Strategy Call
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </nav>
        )}
      </header>
    </>
  );
}
